import { WorkflowTaskFormSubmission } from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskFormSubmission";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { getFilterDisplayValue } from "#src/components/FilterArea/FilterArea.helpers";
import { useListDatasets } from "#src/components/hooks/adapters/useDatasets";
import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import { useGetOneWorkflowTemplate } from "#src/components/hooks/adapters/useWorkflowTemplates";
import useLocalization from "#src/hooks/useLocalization";
import { WorkflowCategoryDetailsRoutePath } from "#src/routes/workflows/categories/[workflowCategoryId]";
import { WorkflowTemplateDetailsRoutePath } from "#src/routes/workflows/templates/[workflowTemplateId]";
import {
  Accordion,
  FilterBuilderFieldsType,
  FilterDisplay,
  Icon,
  KeyValueList,
  KeyValuePairList,
  Skeleton,
  Tooltip,
} from "@validereinc/common-components";
import {
  isTriggerAssetCreate,
  isTriggerAssetUpdate,
  isTriggerCron,
  isTriggerDatasetSubmission,
  isTriggerFormSubmissionCreate,
  isTriggerOtherWorkflowCompletion,
  WorkflowTriggerAssetCreate,
  WorkflowTriggerFormCreate,
  WorkflowTriggerFormCreateAnswerFilterType,
  WorkflowTriggerType,
  type WorkflowTriggerDatasetSubmission,
} from "@validereinc/domain";
import {
  getDatasetSubmissionTriggerDatasetDisplayNames,
  getDatasetSubmissionTriggerDatasetIds,
  getDatasetSubmissionTriggerStatusDisplayLabels,
} from "#src/components/Workflows/WorkflowDatasetSubmissionTrigger.helpers";
import { renderDatasetSubmissionOverviewListValue } from "#src/components/Workflows/WorkflowDatasetSubmissionTrigger.display";
import { cronToSimpleDisplayText } from "@validereinc/utilities";
import { startCase } from "es-toolkit/compat";
import React from "react";

import classNames from "classnames/bind";
import styles from "./WorkflowConfigurationDrawerTriggerDetails.module.scss";

const cx = classNames.bind(styles);

const ScheduledDetails = ({ cron }: { cron: string }) => {
  if (!cron) return null;
  const data = [
    {
      title: cronToSimpleDisplayText(cron),
      value: "",
    },
  ];

  return (
    <Accordion defaultActiveKeys={["details"]}>
      <Accordion.AccordionPanel
        title="Schedule Overview"
        dataKey={"details"}
      >
        <KeyValueList
          data={data}
          color="light"
          variant="shaded"
        />
      </Accordion.AccordionPanel>
    </Accordion>
  );
};

const AssetCreateAndUpdateDetails = ({
  asset_filter,
  asset_type,
  attributes,
  triggerDefinition,
}: {
  asset_filter: WorkflowTriggerAssetCreate["asset_filter"];
  asset_type: WorkflowTriggerAssetCreate["asset_type"];
  attributes?: string[];
  triggerDefinition: WorkflowTriggerType;
}) => {
  const { localize } = useLocalization();

  const assetFilterData: KeyValuePairList = Object.entries(asset_filter)
    .map(([key, value]) => {
      let formattedKey = key;
      if (formattedKey.startsWith("custom_attributes.")) {
        formattedKey = formattedKey.replace("custom_attributes.", "");
      }
      formattedKey = startCase(formattedKey);
      const formattedValue = getFilterDisplayValue(value);
      if (formattedValue.trim()) {
        return {
          title: formattedKey,
          value: formattedValue,
        };
      }
      return;
    })
    .filter((e) => !!e);

  assetFilterData.unshift({ title: "Asset Type", value: localize(asset_type) });

  const hasUpdateAttributes =
    Array.isArray(attributes) && attributes.length > 0;

  return (
    <>
      {isTriggerAssetUpdate(triggerDefinition) && (
        <Accordion defaultActiveKeys={["trigger-attributes"]}>
          <Accordion.AccordionPanel
            title={
              <div className={cx("accordion-with-tooltip")}>
                Trigger Attributes
                <Tooltip
                  content={
                    <p>
                      Trigger attributes represent the attributes which when
                      changed, will trigger a workflow.
                      <br />
                      <br />
                      The asset must also fall under the trigger filter
                      criteria.
                    </p>
                  }
                >
                  <Icon
                    variant="info"
                    size={18}
                    className={cx("icon")}
                  />
                </Tooltip>
              </div>
            }
            dataKey={"trigger-attributes"}
          >
            <KeyValueList
              data={
                hasUpdateAttributes
                  ? attributes.map((attribute) => ({
                      title: attribute,
                      value: "",
                    }))
                  : [{ title: "All attributes", value: "" }]
              }
              color="light"
              variant="shaded"
            />
          </Accordion.AccordionPanel>
        </Accordion>
      )}
      <Accordion defaultActiveKeys={["trigger-filters"]}>
        <Accordion.AccordionPanel
          title="Trigger Filters"
          dataKey={"trigger-filters"}
        >
          <KeyValueList
            data={assetFilterData}
            color="light"
            variant="shaded"
          />
        </Accordion.AccordionPanel>
      </Accordion>
    </>
  );
};

const FormSubmissionCreateDetails = ({
  form_schema_id,
  answer_filter,
}: {
  form_schema_id: string;
  answer_filter?: WorkflowTriggerFormCreate["answer_filter"];
}) => {
  const formSchemaQuery = useGetOneFormSchema(
    { id: form_schema_id },
    { enabled: !!form_schema_id }
  );

  const formSchema = formSchemaQuery.data?.data;

  if (!answer_filter || !formSchema) {
    return (
      <WorkflowTaskFormSubmission
        formSchemaId={form_schema_id}
        color="light"
      />
    );
  }

  let answerFilterArray: WorkflowTriggerFormCreateAnswerFilterType[] = [];
  if (answer_filter) {
    answerFilterArray = Array.isArray(answer_filter)
      ? answer_filter
      : [answer_filter];
  }

  /**
   * Transform answer filter to FilterBuilder format and collect field definitions
   */
  const transformFilterAndCollectFields = (): {
    filter: Record<string, unknown>;
    fields: FilterBuilderFieldsType;
  } => {
    const fields: FilterBuilderFieldsType = {};
    let filterIndex = 0;

    const transformSingleFilter = (
      filterItem: Record<string, unknown>
    ): Record<string, unknown> => {
      // Handle logical operators ($or, $and)
      if ("$or" in filterItem && Array.isArray(filterItem.$or)) {
        return {
          $or: filterItem.$or.map((nested) =>
            transformSingleFilter(nested as Record<string, unknown>)
          ),
        };
      }

      if ("$and" in filterItem && Array.isArray(filterItem.$and)) {
        return {
          $and: filterItem.$and.map((nested) =>
            transformSingleFilter(nested as Record<string, unknown>)
          ),
        };
      }

      // Handle regular filter with question_id
      if (
        "question_id" in filterItem &&
        typeof filterItem.question_id === "string"
      ) {
        const questionId = filterItem.question_id;
        const question = formSchema.config.questions?.[questionId];

        if (question?.prompt) {
          // Create a unique field key for each question occurrence
          const fieldKey = `${questionId}_${filterIndex++}`;

          // Map form schema data type to FilterBuilder field definition
          const questionDataType =
            "data_type" in question ? (question.data_type as string) : "string";

          let fieldDefinition: FilterBuilderFieldsType[string];

          switch (questionDataType) {
            case "number":
            case "integer":
              fieldDefinition = {
                data_type: questionDataType as "number" | "integer",
                display_name: question.prompt,
              };
              break;
            case "boolean":
              fieldDefinition = {
                data_type: "boolean",
                display_name: question.prompt,
              };
              break;
            case "pick-list":
            case "multi-pick-list": {
              // Try to get pick_list_values from question schema
              const pickListValues =
                "pick_list_values" in question &&
                Array.isArray(question.pick_list_values)
                  ? question.pick_list_values
                  : [];

              if (pickListValues.length > 0) {
                fieldDefinition = {
                  data_type: "pick-list",
                  display_name: question.prompt,
                  pick_list_options: pickListValues,
                };
              } else {
                // Fall back to mixed if no pick list values available
                fieldDefinition = {
                  data_type: "mixed",
                  display_name: question.prompt,
                };
              }
              break;
            }
            case "lookup":
            case "multi-lookup":
              // Lookups don't have pick_list_values, use mixed type
              fieldDefinition = {
                data_type: "mixed",
                display_name: question.prompt,
              };
              break;
            default:
              fieldDefinition = {
                data_type: "string",
                display_name: question.prompt,
              };
          }

          fields[fieldKey] = fieldDefinition;

          return {
            [fieldKey]: filterItem["answer.value"],
          };
        }
      }

      return {};
    };

    // If multiple filters, wrap in $and
    const transformedFilter =
      answerFilterArray.length === 1
        ? transformSingleFilter(answerFilterArray[0] as Record<string, unknown>)
        : {
            $and: answerFilterArray.map((filter) =>
              transformSingleFilter(filter as Record<string, unknown>)
            ),
          };

    return { filter: transformedFilter, fields };
  };

  const { filter, fields } = transformFilterAndCollectFields();

  return (
    <>
      <WorkflowTaskFormSubmission
        formSchemaId={form_schema_id}
        color="light"
      />
      {answerFilterArray.length > 0 && (
        <Accordion defaultActiveKeys={["trigger-questions"]}>
          <Accordion.AccordionPanel
            title="Trigger Questions"
            dataKey="trigger-questions"
          >
            <FilterDisplay
              variant="vertical"
              fields={fields}
              filter={filter}
            />
          </Accordion.AccordionPanel>
        </Accordion>
      )}
    </>
  );
};

const DatasetSubmissionCreateDetails = ({
  dataset_id,
  status_filter,
}: {
  dataset_id: WorkflowTriggerDatasetSubmission["dataset_id"];
  status_filter?: WorkflowTriggerDatasetSubmission["status_filter"];
}) => {
  const { data: datasetsData, isLoading: isLoadingDatasets } =
    useListDatasets();
  const datasetNameById = Object.fromEntries(
    datasetsData?.data?.map((dataset) => [dataset.id, dataset.name]) ?? []
  );
  const datasetNames = getDatasetSubmissionTriggerDatasetDisplayNames(
    dataset_id,
    datasetNameById
  );
  const statusLabels =
    getDatasetSubmissionTriggerStatusDisplayLabels(status_filter);

  const overviewData: KeyValuePairList = [
    {
      title: "Dataset",
      value: renderDatasetSubmissionOverviewListValue(datasetNames),
    },
    {
      title: "Dataset Submission Statuses",
      value: renderDatasetSubmissionOverviewListValue(statusLabels),
    },
  ];

  return (
    <Skeleton isLoading={isLoadingDatasets}>
      <Accordion defaultActiveKeys={["details"]}>
        <Accordion.AccordionPanel
          title="Dataset Submission Overview"
          dataKey={"details"}
        >
          <KeyValueList
            className={cx("datasetSubmissionOverviewKeyValueList")}
            data={overviewData}
            color="light"
            variant="shaded"
          />
        </Accordion.AccordionPanel>
      </Accordion>
    </Skeleton>
  );
};

const OtherWorkflowCreationDetails = ({
  workflow_template_id,
}: {
  workflow_template_id: string;
}) => {
  const workflowTemplateQuery = useGetOneWorkflowTemplate(
    { id: workflow_template_id },
    { enabled: !!workflow_template_id }
  );

  const workflowTemplate = workflowTemplateQuery.data?.data;
  const isLoading = workflowTemplateQuery.isFetching && !!workflowTemplate;

  return (
    <Skeleton
      isLoading={isLoading}
      className={cx("other-workflow-skeleton")}
    >
      {!isLoading && workflowTemplate && (
        <Accordion defaultActiveKeys={["details"]}>
          <Accordion.AccordionPanel
            title="Other Workflow Overview"
            dataKey={"details"}
          >
            <KeyValueList
              data={[
                ...(workflowTemplate?.workflow_category
                  ? [
                      {
                        title: "Workflow Category",
                        value: (
                          <RoutingLink
                            to={WorkflowCategoryDetailsRoutePath.toLinkParts({
                              pathParams: {
                                workflowCategoryId:
                                  workflowTemplate.category_id,
                              },
                            })}
                          >
                            {workflowTemplate.workflow_category.name}
                          </RoutingLink>
                        ),
                      },
                    ]
                  : []),
                {
                  title: "Workflow Template",
                  value: (
                    <RoutingLink
                      to={WorkflowTemplateDetailsRoutePath.toLinkParts({
                        pathParams: {
                          workflowTemplateId: workflowTemplate.id,
                        },
                      })}
                    >
                      {workflowTemplate.name}
                    </RoutingLink>
                  ),
                },
              ]}
              color="light"
              variant="shaded"
            />
          </Accordion.AccordionPanel>
        </Accordion>
      )}
    </Skeleton>
  );
};

const WorkflowConfigurationDrawerTriggerDetails = (props: {
  triggerDefinition?: WorkflowTriggerType;
}) => {
  const { triggerDefinition } = props;

  if (!triggerDefinition) return null;

  return (
    <>
      {isTriggerFormSubmissionCreate(triggerDefinition) && (
        <FormSubmissionCreateDetails
          form_schema_id={triggerDefinition.form_schema_id}
          answer_filter={triggerDefinition.answer_filter}
        />
      )}

      {isTriggerOtherWorkflowCompletion(triggerDefinition) && (
        <OtherWorkflowCreationDetails
          workflow_template_id={triggerDefinition.workflow_template_id}
        />
      )}

      {isTriggerDatasetSubmission(triggerDefinition) &&
        getDatasetSubmissionTriggerDatasetIds(triggerDefinition.dataset_id)
          .length > 0 && (
          <DatasetSubmissionCreateDetails
            dataset_id={triggerDefinition.dataset_id}
            status_filter={triggerDefinition.status_filter}
          />
        )}

      {(isTriggerAssetCreate(triggerDefinition) ||
        isTriggerAssetUpdate(triggerDefinition)) && (
        <AssetCreateAndUpdateDetails
          asset_type={triggerDefinition.asset_type}
          asset_filter={triggerDefinition.asset_filter}
          triggerDefinition={triggerDefinition}
          attributes={
            isTriggerAssetUpdate(triggerDefinition)
              ? triggerDefinition.attributes
              : undefined
          }
        />
      )}

      {isTriggerCron(triggerDefinition) && (
        <ScheduledDetails cron={triggerDefinition.cron ?? ""} />
      )}
    </>
  );
};

export default WorkflowConfigurationDrawerTriggerDetails;
